import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Página aberta ao ler o QR do equipamento — licencia em 1 toque.
export default function LicenseDevice() {
  const { deviceId } = useParams();
  const [state, setState] = useState('loading'); // loading | unauth | notfound | ready | done
  const [device, setDevice] = useState(null);
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);

  const fetchDevice = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/oem-devices`, { withCredentials: true });
      const d = (data || []).find((x) => x.device_id === deviceId);
      if (!d) { setState('notfound'); return; }
      setDevice(d);
      setLabel(d.label || d.model || '');
      setState(d.status === 'licensed' ? 'done' : 'ready');
    } catch (e) {
      setState(e.response?.status === 401 || e.response?.status === 403 ? 'unauth' : 'notfound');
    }
  }, [deviceId]);

  useEffect(() => { fetchDevice(); }, [fetchDevice]);

  const license = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/oem-devices/${deviceId}/license`, { label }, { withCredentials: true });
      toast.success('Equipamento licenciado 💎');
      setState('done');
      fetchDevice();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao licenciar');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-6" data-testid="license-device-page">
      <div className="w-full max-w-sm bg-slate-900 border border-slate-800 rounded-2xl p-6 text-center space-y-4">
        <h1 className="text-2xl font-black">📟 Licenciar Equipamento</h1>
        {state === 'loading' && <p className="text-slate-400">A verificar...</p>}
        {state === 'unauth' && (
          <>
            <p className="text-slate-300 text-sm">Precisas de sessão de <strong>admin</strong> neste browser.</p>
            <Button className="w-full" onClick={() => { window.location.href = `/login?next=/license-device/${deviceId}`; }} data-testid="license-login-btn">
              Fazer login
            </Button>
          </>
        )}
        {state === 'notfound' && (
          <p className="text-amber-400 text-sm" data-testid="license-notfound">
            Equipamento ainda não visto pelo servidor. Abre a app nesse equipamento (com login e internet) e volta a ler o QR.
          </p>
        )}
        {state === 'ready' && device && (
          <>
            <p className="text-slate-300 text-sm">{device.model || 'Android'}<br /><span className="text-slate-500 text-xs">ID {device.device_id.slice(0, 16)}…</span></p>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Etiqueta (cliente/veículo)"
              className="bg-slate-800 border-slate-700 text-white"
              data-testid="license-label-input"
            />
            <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-lg font-bold h-12" onClick={license} disabled={busy} data-testid="license-confirm-btn">
              {busy ? 'A licenciar...' : '💎 Licenciar Premium'}
            </Button>
          </>
        )}
        {state === 'done' && (
          <div data-testid="license-done">
            <p className="text-5xl">✅</p>
            <p className="text-emerald-400 font-bold mt-2">Equipamento licenciado!</p>
            <p className="text-slate-400 text-xs mt-2">Qualquer conta que faça login neste equipamento fica Premium automaticamente.</p>
          </div>
        )}
      </div>
    </div>
  );
}
